import { ArrayDataFlowType, IntegerDataFlowType } from 'app/model/dataflow/dataflow-datatype';
import { DataFlowSink } from 'app/model/dataflow/dataflow-sink';
import { DataFlowSource, IsolationLevel } from 'app/model/dataflow/dataflow-source';
import { PostgreSqlSettings } from 'app/model/dataflow/dataset-settings/postgre-sql-settings';
import { DataFlowDatasetSettings } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { getAttributeValueFromDataset } from 'app/model/dataset/dataset.utils';
import { Dataset, DataFlowDSLAdapterRegistry, LinkedService, Metamodel, Owned } from 'shell-models-lib';
import { DataFlowExpressionUtils } from '../expression.util';
import { DataFlowAdapterUtils } from '../adapter.util';
import { TabularTypeAdapter } from './tabular-type.adapter';

@DataFlowDSLAdapterRegistry.adapter({
    model: Metamodel.modelFor(PostgreSqlSettings)
})
export class PostgreSqlAdapter extends TabularTypeAdapter {
    /**
     * Supplements missing DSL properties for Fabric Migration.
     */
    public supplementPropsForFabricMigration(dataset: Dataset | undefined, _linkedService: LinkedService | undefined, props: string[], _isSource: boolean, isInline: boolean, _datasetSettings: DataFlowDatasetSettings | undefined): void {
        if (isInline || !dataset) {
            return;
        }

        // Only add tableName and schemaName for 'table' format
        const formatProp = props.find((p) => p && p.substring(0, p.indexOf(':')) === 'format');
        const isTableFormat = formatProp?.includes('\'table\'');
        if (isTableFormat) {
            const schema = getAttributeValueFromDataset(dataset, 'schema');
            if (schema) {
                props.push(`schemaName: ${DataFlowAdapterUtils.formatExpressionValue(schema)}`);
            }

            const tableName = getAttributeValueFromDataset(dataset, 'tableName') || getAttributeValueFromDataset(dataset, 'table');
            if (tableName) {
                props.push(`tableName: ${DataFlowAdapterUtils.formatExpressionValue(tableName)}`);
            }
        }
    }

    public async toSourceDSL(setting: PostgreSqlSettings): Promise<string[]> {
        const props = await super.toSourceDSL(setting);
        props.push(`store: 'postgres'`);
        setting.isolationLevel.expression = setting.isolationLevel.expression || IsolationLevel.ReadUncommitted;
        props.push(`isolationLevel: ${DataFlowExpressionUtils.wrapExpressionConfigValue(setting.isolationLevel)}`);
        if (setting.batchSize.expression) {
            props.push(
                `batchSize: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    setting.batchSize,
                    IntegerDataFlowType.TYPE
                )}`
            );
        }
        const source = setting.owner as DataFlowSource;
        if (source.partitionOption === 'Partitioning' && source.partitionMethod === 'external') {
            const validExps = (source.partitionPredicates || []).filter(
                (exp) => exp.expression != null && exp.expression !== ''
            );
            if (validExps.length > 0) {
                const result = validExps
                    .map((s) => `'${DataFlowExpressionUtils.escapeSingleQuoteInDslString(s.expression)}'`)
                    .join(',');
                props.push(`partitionPredicates: [${result}]`);
            }
        }
        return props;
    }

    public async toSinkDSL(setting: PostgreSqlSettings): Promise<string[]> {
        const props = await super.toSinkDSL(setting);
        props.push(`store: 'postgres'`);
        if (setting.batchSize.expression) {
            props.push(
                `batchSize: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    setting.batchSize,
                    IntegerDataFlowType.TYPE
                )}`
            );
        }
        switch (setting.isPreSqlsListOfExpression) {
            case false:
                if (setting.preSqls && setting.preSqls.length && setting.preSqls[0].expression) {
                    props.push(
                        `preSQLs: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            setting.preSqls[0],
                            ArrayDataFlowType.TYPE
                        )}`
                    );
                }
                break;
            default:
                const preSQLScripts = (setting.preSqls || [])
                    .filter((script) => !!script.expression)
                    .map((script) => DataFlowExpressionUtils.escapeAndWrapConfigValue(script))
                    .join(',');
                if (!!preSQLScripts) {
                    props.push(`preSQLs:[${preSQLScripts}]`);
                }
        }
        switch (setting.isPostSqlsListOfExpression) {
            case false:
                if (setting.postSqls && setting.postSqls.length && setting.postSqls[0].expression) {
                    props.push(
                        `postSQLs: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            setting.postSqls[0],
                            ArrayDataFlowType.TYPE
                        )}`
                    );
                }
                break;
            default:
                const postSQLScripts = (setting.postSqls || [])
                    .filter((script) => !!script.expression)
                    .map((script) => DataFlowExpressionUtils.escapeAndWrapConfigValue(script))
                    .join(',');
                if (!!postSQLScripts) {
                    props.push(`postSQLs:[${postSQLScripts}]`);
                }
        }
        return props;
    }

    public async toSourceModel(
        owner: DataFlowSource,
        configs: Map<string, string>,
        handledConfigs: string[]
    ): Promise<PostgreSqlSettings> {
        const setting = (await super.toSourceModel(owner, configs, handledConfigs)) as PostgreSqlSettings;
        if (configs.has('isolationLevel')) {
            setting.isolationLevel = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('isolationLevel'),
                owner
            );
            handledConfigs.push('isolationLevel');
        }
        if (configs.has('batchSize')) {
            setting.batchSize = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('batchSize'),
                owner,
                IntegerDataFlowType.TYPE
            );
            handledConfigs.push('batchSize');
        }
        return setting;
    }

    public async toSinkModel(
        owner: DataFlowSink,
        configs: Map<string, string>,
        handledConfigs: string[]
    ): Promise<PostgreSqlSettings> {
        const setting = (await super.toSinkModel(owner, configs, handledConfigs)) as PostgreSqlSettings;
        if (configs.has('batchSize')) {
            setting.batchSize = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('batchSize'),
                owner,
                IntegerDataFlowType.TYPE
            );
            handledConfigs.push('batchSize');
        }
        if (configs.has('preSQLs')) {
            const expression = configs.get('preSQLs');
            if (expression.startsWith('[') && expression.endsWith(']')) {
                setting.isPreSqlsListOfExpression = true;
                const expressionArray = DataFlowExpressionUtils.parseExpressionArrayInDsl(expression, owner);
                if (expressionArray.length > 0) {
                    setting.preSqls = expressionArray.map((expression) => {
                        if (!expression.templateExpression) {
                            DataFlowExpressionUtils.unescapeConfigValue(expression);
                        }
                        return expression;
                    });
                }
            } else {
                setting.isPreSqlsListOfExpression = false;
                setting.preSqls = [
                    DataFlowExpressionUtils.unwrapExpressionConfigValue(expression, owner, ArrayDataFlowType.TYPE)
                ];
            }
            handledConfigs.push('preSQLs');
        }
        if (configs.has('postSQLs')) {
            const expression = configs.get('postSQLs');
            if (expression.startsWith('[') && expression.endsWith(']')) {
                setting.isPostSqlsListOfExpression = true;
                const expressionArray = DataFlowExpressionUtils.parseExpressionArrayInDsl(expression, owner);
                if (expressionArray.length > 0) {
                    setting.postSqls = expressionArray.map((expression) => {
                        if (!expression.templateExpression) {
                            DataFlowExpressionUtils.unescapeConfigValue(expression);
                        }
                        return expression;
                    });
                }
            } else {
                setting.isPostSqlsListOfExpression = false;
                setting.postSqls = [
                    DataFlowExpressionUtils.unwrapExpressionConfigValue(expression, owner, ArrayDataFlowType.TYPE)
                ];
            }
        }
        return setting;
    }

    protected _generateSetting(owner: DataFlowSource | DataFlowSink): PostgreSqlSettings {
        const result = Owned.create<PostgreSqlSettings>(PostgreSqlSettings.MODEL, owner);
        result.initialize(undefined);
        return result;
    }
}
